import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '@/v2/contexts/AuthContext'
import { getUserFriendlyErrorMessage } from '@/v2/utils/errorMessages'

const meta = document.querySelector('meta[name=csrf-token]')
export const csrfToken = meta && meta.getAttribute('content')

export const baseURL = window.location.origin

const isDevEnvironment =
  window.location.port !== '' ||
  window.location.hostname.endsWith('.local') ||
  window.location.hostname.endsWith('.lvh.me')

export const wsUrl = isDevEnvironment
  ? `ws://${window.location.host}/cable`
  : `wss://${window.location.host}/cable`

export const headers = {
  'Content-Type': 'application/json',
  'X-CSRF-Token': csrfToken,
}

export const STALE_TIME = 5 * 60 * 1000 // 5 minutes

export const requestMiddleware = requestInfo => {
  const { request, key, onSuccess } = requestInfo
  const { onOpenSnackbar } = useSnackbarContext()
  const navigate = useNavigate()
  const { setUser, setIsLoggedIn } = useAuthContext()
  const mutation = useMutation({
    mutationKey: key,
    mutationFn: async variables => {
      try {
        const result = await request(variables)
        if (result.status === 204) {
          return {}
        }
        const data = await result.json()

        if (!result.ok) {
          const { errors, error, ...others } = data

          if (typeof errors === 'string') {
            onOpenSnackbar(getUserFriendlyErrorMessage(errors), 'error')
            return { error: errors }
          }

          if (typeof error === 'string') {
            onOpenSnackbar(getUserFriendlyErrorMessage(error), 'error')
            return { error }
          }

          const fields = Object.keys(errors || others)

          if (Array.isArray(errors)) {
            errors.forEach(errMsg => onOpenSnackbar(errMsg, 'error'))
            return { error: errors }
          } else if (errors && typeof errors === 'object') {
            const fieldError =
              fields.length && errors[fields[0]]
                ? `${fields[0]}: ${errors[fields[0]][0]}`
                : null

            if (fieldError) {
              onOpenSnackbar(getUserFriendlyErrorMessage(fieldError), 'error')
            }

            return {
              error: fields.length
                ? {
                    [fields[0]]: errors[fields[0]]?.[0],
                  }
                : errors,
            }
          } else if (others && fields.length) {
            const othersError = others[fields[0]]?.[0] || others[fields[0]]

            if (othersError) {
              onOpenSnackbar(getUserFriendlyErrorMessage(othersError), 'error')
            }

            return {
              error: {
                [fields[0]]: othersError,
              },
            }
          }

          const defaultErrorMessage =
            'An unexpected error occurred. Please try again.'
          onOpenSnackbar(
            getUserFriendlyErrorMessage(defaultErrorMessage),
            'error'
          )
          return { error: defaultErrorMessage }
        }

        return data
      } catch (e) {
        console.error(e)
        const errorMessage =
          e.message ||
          'Network error. Please check your connection and try again.'
        throw new Error(errorMessage)
      }
    },
    onSuccess,
    onError: error => {
      if (error.status === 401) {
        setUser(null)
        setIsLoggedIn(false)
        navigate('/v2/login', { replace: true })
      }
      console.error(error.status, error)
      const errorMessage =
        error.message ||
        `Request failed with status ${error.status}. Please try again.`
      onOpenSnackbar(errorMessage, 'error')
    },
  })

  return mutation
}
